/**
 * Abstração de Manipulação de Ficheiros
 * Centraliza downloads e visualização de Blobs.
 */

import { isNative } from '../config/env';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const res = reader.result as string;
      const base64 = res.includes(',') ? res.split(',')[1] : res;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

export const downloadBlob = async (blob: Blob, filename: string): Promise<void> => {
  try {
    if (isNative) {
      const base64Data = await blobToBase64(blob);
      const safeFilename = filename.replace(/[/\\?%*:|"<>]/g, '_');
      const savedFile = await Filesystem.writeFile({
        path: safeFilename,
        data: base64Data,
        directory: Directory.Cache,
      });
      await Share.share({
        title: safeFilename,
        url: savedFile.uri,
        dialogTitle: 'Guardar ou partilhar documento',
      });
      return;
    }
  } catch (err) {
    console.warn('[FileService] Erro no processamento nativo de download, tentando fallback web:', err);
  }

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();

  // Limpeza após delay para garantir que o browser iniciou o download
  window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
};

export const viewBlob = async (blob: Blob, filename = 'documento.pdf'): Promise<void> => {
  try {
    if (isNative) {
      await downloadBlob(blob, filename);
      return;
    }
  } catch (err) {
    console.warn('[FileService] Erro no processamento nativo de visualização, tentando fallback web:', err);
  }

  const url = window.URL.createObjectURL(blob);
  const win = window.open(url, '_blank', 'noopener,noreferrer');

  if (!win) {
    // Se o popup for bloqueado, tenta download como fallback
    downloadBlob(blob, filename);
    return;
  }

  window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
};

/**
 * Auxiliar para obter nome do ficheiro do header Content-Disposition
 */
export const getFilenameFromHeader = (contentDisposition?: string, fallback = 'documento'): string => {
  if (!contentDisposition) return fallback;
  const match = contentDisposition.match(/filename="?([^"]+)"?/i);
  return match ? match[1] : fallback;
};
